var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _onKeyUp, _HeaderDrawer_instances, getDetailsElement_fn, close_fn, setupAnimatedElementListeners_fn;
import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from "@theme/utilities";
class HeaderDrawer extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _HeaderDrawer_instances);
    __publicField(this, "requiredRefs", ["details", "menuDrawer"]);
    /**
     * Close the main menu drawer when the Escape key is pressed
     * @param {KeyboardEvent} event
     */
    __privateAdd(this, _onKeyUp, (event) => {
      if (event.key !== "Escape") return;
      __privateMethod(this, _HeaderDrawer_instances, close_fn).call(this, __privateMethod(this, _HeaderDrawer_instances, getDetailsElement_fn).call(this, event));
    });
    /**
     * Prevents the drawer search form from submitting when the search term is empty.
     * @param {Event} event - The submit event.
     */
    __publicField(this, "handleSearchSubmit", (event) => {
      const input = this.querySelector(".menu-drawer__search-input");
      if (!(input instanceof HTMLInputElement) || input.value.trim()) return;
      event.preventDefault();
      input.focus();
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keyup", __privateGet(this, _onKeyUp));
    __privateMethod(this, _HeaderDrawer_instances, setupAnimatedElementListeners_fn).call(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", __privateGet(this, _onKeyUp));
  }
  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }
  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }
  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = __privateMethod(this, _HeaderDrawer_instances, getDetailsElement_fn).call(this, event);
    const summary = details.querySelector("summary");
    if (!summary) return;
    summary.setAttribute("aria-expanded", "true");
    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add("menu-open");
      if (target) {
        this.refs.menuDrawer.classList.add("menu-drawer--has-submenu-opened");
      }
      const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }
  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    __privateMethod(this, _HeaderDrawer_instances, close_fn).call(this, __privateMethod(this, _HeaderDrawer_instances, getDetailsElement_fn).call(this, event));
  }
  /**
   * Close the main menu drawer
   */
  close() {
    __privateMethod(this, _HeaderDrawer_instances, close_fn).call(this, this.refs.details);
  }
  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll("accordion-custom .details-content");
    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add("details-content--no-animation");
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove("details-content--no-animation");
        }
      });
    }, 100);
  }
}
_onKeyUp = new WeakMap();
_HeaderDrawer_instances = new WeakSet();
/**
 * Get the closest details element to the event target
 * @param {Event | undefined} event
 * @returns {HTMLDetailsElement}
 */
getDetailsElement_fn = function(event) {
  if (!(event?.target instanceof Element)) return this.refs.details;
  return event.target.closest("details") ?? this.refs.details;
};
/**
 * Close the closest menu or submenu that is open
 *
 * @param {HTMLDetailsElement} details
 */
close_fn = function(details) {
  const summary = details.querySelector("summary");
  if (!summary) return;
  summary.setAttribute("aria-expanded", "false");
  details.classList.remove("menu-open");
  this.refs.menuDrawer.classList.remove("menu-drawer--has-submenu-opened");
  const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");
  onAnimationEnd(
    drawer || details,
    () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]:not(accordion-custom > details)");
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    },
    { subtree: false }
  );
};
/**
 * Attach animationend event listeners to all animated elements to remove will-change after animation
 * to remove the stacking context and allow submenus to be positioned correctly
 */
setupAnimatedElementListeners_fn = function() {
  const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
  allAnimated.forEach((element) => {
    element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
  });
};
if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
